import { Observable, of, throwError } from 'rxjs';
import { AjaxError } from 'rxjs/ajax';
import { catchError, map, tap } from 'rxjs/operators';
import { ExtensionBrokerQuery } from '../data/extension-broker/extension-broker';
import { GatewayConnection } from '../data/gateway-connection';
import { HttpStatusCode } from '../data/http-constants';
import { Net } from '../data/net';
import { NodeConnection, NodeRequest } from '../data/node-connection';
import { PowerShellCommand } from '../data/powershell';
import { PowerShellConnection } from '../data/powershell-connection';
import { LogLevel } from '../diagnostics/log-level';
import { Logging } from '../diagnostics/logging';
import { Strings } from '../generated/strings';
import { EnvironmentModule, EnvironmentModuleConnectionStatusProvider, ExtensionMethodIdentifier } from '../manifest/environment-modules';
import { Rpc } from '../rpc/rpc';
import { AuthorizationManager } from './authorization-manager';
import { Connection, ConnectionUtility } from './connection';
import { ConnectionManager } from './connection-manager';

/**
 * Live connection interface.
 */
export interface LiveConnection {
    /**
     * the connection object.
     */
    connection: Connection;

    /**
     * the loading state while query the connection status.
     */
    loading: boolean;

    /**
     * the date number (Date.now() value).
     */
    lastUpdated: number;

    /**
     * The PowerShell connection.
     */
    isPowerShell: boolean;

    /**
     * The endpoint of PowerShell.
     */
    powerShellEndpoint: string;

    /**
     * the status of connection.
     */
    status?: LiveConnectionStatus;

    /**
     * The extra properties on the connection.
     */
    properties?: MsftSme.StringMap<any>;
}

/**
 * The live connection status.
 */
export interface LiveConnectionStatus {
    /**
     * The display string of status.
     */
    label?: string;

    /**
     * The status type.
     */
    type: LiveConnectionStatusType;

    /**
     * The detail connection error message.
     */
    details?: string;
}

/**
 * The live connection status type.
 */
export enum LiveConnectionStatusType {
    /**
     * Online status.
     */
    Online = 0,

    /**
     * Warning status.
     */
    Warning = 1,

    /**
     * Unauthorized status.
     */
    Unauthorized = 2,

    /**
     * Error status.
     */
    Error = 3,

    /**
     * Fatal status.
     */
    Fatal = 4,

    /**
     * Unknown status (used for loading status).
     */
    Unknown = 5,

    /**
     * Forbidden status.
     */
    Forbidden = 6
}

export interface ConnectionStatusResult extends MsftSme.StringMap<any> {
    status?: LiveConnectionStatus;
    aliases?: string[];
}

/**
 * ConnectionStream class that enables to get all connections once and listen to the change.
 *
 * TODO:
 * 1. Support live connection status for a single connection in such a way that one could subscribe to it from ActiveConnection
 *    with that observable always being for the active connection.
 * 2. Support updating all connection status on an interval. (using existing expiration field in cache)
 * 3. Support preserving status across sessions during the interval time.
 *    currently we are using session storage, this may also require credentials to be preserved across sessions.
 */
export class ConnectionStream {
    private statusLabelMap: { [index: number]: string } = {};
    private connectionStrings = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Connection;
    private cacheLiveConnection: LiveConnection;

    /**
     * Initializes a new instance of the ConnectionStream class.
     * @param connectionManager the connection manager object.
     * @param powershellConnection the powerShell connection object.
     * @param gatewayConnection the gateway connection object.
     */
    constructor(
        private rpc: Rpc,
        private connectionManager: ConnectionManager,
        private powershellConnection: PowerShellConnection,
        private gatewayConnection: GatewayConnection,
        private nodeConnection: NodeConnection,
        private authorizationManager: AuthorizationManager,
        private extensionBroker: ExtensionBrokerQuery) {
        this.statusLabelMap[LiveConnectionStatusType.Error] = this.connectionStrings.ErrorState.label;
        this.statusLabelMap[LiveConnectionStatusType.Fatal] = this.connectionStrings.FatalState.label;
        this.statusLabelMap[LiveConnectionStatusType.Online] = this.connectionStrings.OnlineState.label;
        this.statusLabelMap[LiveConnectionStatusType.Unauthorized] = this.connectionStrings.NeedsAuthorizationState.label;
        this.statusLabelMap[LiveConnectionStatusType.Unknown] = this.connectionStrings.UnknownState.label;
        this.statusLabelMap[LiveConnectionStatusType.Warning] = this.connectionStrings.WarningState.label;
    }

    /**
     * Wraps a connection in a live connection object by retrieving its current status
     * @param connection the connection object.
     */
    public getLiveConnection(connection: Connection): Observable<LiveConnection> {
        // get the connection types status provider
        const typeInfo = ConnectionUtility.getConnectionTypeInfo(connection);
        if (typeInfo && typeInfo.provider && typeInfo.provider.connectionStatusProvider) {
            const now = Date.now();
            const statusProvider = typeInfo.provider.connectionStatusProvider;

            if (statusProvider.skipStatusCheck) {
                return of(<LiveConnection>{
                    connection: connection,
                    loading: false,
                    status: {
                        type: LiveConnectionStatusType.Online
                    },
                    isPowerShell: false,
                    powerShellEndpoint: null
                });
            }

            if (this.cacheLiveConnection
                && this.cacheLiveConnection.status.type === LiveConnectionStatusType.Online
                && this.cacheLiveConnection.connection
                && this.cacheLiveConnection.connection.name === connection.name
                && this.cacheLiveConnection.connection.type === connection.type
                && now - this.cacheLiveConnection.lastUpdated < 2000) {
                return of(this.cacheLiveConnection);
            } else {
                return this.getConnectionStatus(statusProvider, connection, connection.name)
                    .pipe(
                        map(data => {
                            this.cacheLiveConnection = data;
                            return data;
                        }));
            }
        }

        // this should not happen, it means we have a malformed manifest or the user has uninstalled the relevant connection manager extension.
        const logMessage = this.connectionStrings.NoStatusProvider.message.format(connection.type);

        // log warning about this condition
        Logging.log({
            source: 'ConnectionStream',
            level: LogLevel.Warning,
            message: logMessage
        });

        // we dont need to fail, we can set this items status to unknown and continue
        const statusLabel = this.connectionStrings.NoStatusProvider.label;
        return of(<LiveConnection>{
            connection: connection,
            loading: false,
            status: {
                label: statusLabel,
                type: LiveConnectionStatusType.Unknown,
                details: logMessage
            },
            isPowerShell: false,
            powerShellEndpoint: null
        });
    }

    /**
     * Get connection status and aliases from statusProvider, retry alaises when connection nodeName NotFound
     * @param statusProvider status provider from typeInfo manifest
     * @param connection original connection
     * @param nodeName retry nodeName, can be connection.name or alias
     */
    private getConnectionStatus(
        statusProvider: EnvironmentModuleConnectionStatusProvider,
        connection: Connection,
        nodeName: string): Observable<LiveConnection> {
        // collect the status data from the status provider
        let statusAwaiter: Observable<ConnectionStatusResult>;
        if (statusProvider.powerShell) {
            statusAwaiter = this.getConnectionStatusFromPowershell(nodeName, statusProvider.powerShell);
        } else if (statusProvider.relativeGatewayUrl) {
            statusAwaiter = this.getConnectionStatusFromGatewayUrl(nodeName, statusProvider.relativeGatewayUrl);
        } else if (statusProvider.service) {
            statusAwaiter = this.getConnectionStatusFromService(nodeName, statusProvider.service);
        } else if (statusProvider.worker) {
            statusAwaiter = this.getConnectionStatusFromWorker(nodeName, statusProvider.worker);
        }

        return statusAwaiter
            .pipe(
                map(statusData => {
                    // create connection object to return
                    const liveConnection: LiveConnection = {
                        connection: connection,
                        loading: false,
                        lastUpdated: Date.now(),
                        isPowerShell: !!statusProvider.powerShell,
                        powerShellEndpoint: statusProvider.powerShell && this.authorizationManager.getJeaEndpoint(connection.name)
                    };
                    // extract the status field from the data
                    if (statusData.status) {
                        liveConnection.status = statusData.status;
                        delete statusData.status;
                    } else {
                        // if there is no status field, assume everything is good since we got this far.
                        liveConnection.status = {
                            type: LiveConnectionStatusType.Online
                        };
                    }
                    // load localized values for label and details
                    if (statusProvider.displayValueMap) {
                        const label = statusProvider.displayValueMap[liveConnection.status.label];
                        const details = statusProvider.displayValueMap[liveConnection.status.details];
                        liveConnection.status.label = label || liveConnection.status.label;
                        liveConnection.status.details = details || liveConnection.status.details;
                    }
                    // extract the aliases field from the data
                    if (statusData.aliases) {
                        this.connectionManager.saveAliasesData(statusData.aliases, connection, nodeName);
                        delete statusData.aliases;
                    }
                    // any other properties returned by status call live in property bag.
                    liveConnection.properties = statusData;

                    // fill properties of connection with status data.
                    connection.properties = connection.properties || {};
                    Object.assign(connection.properties, statusData);

                    // return the new live connection
                    return liveConnection;
                }),
                catchError((error: AjaxError) => {
                    const liveConnection = <LiveConnection>{
                        connection: connection,
                        loading: false,
                        lastUpdated: Date.now(),
                        isPowerShell: !!statusProvider.powerShell,
                        powerShellEndpoint: statusProvider.powerShell && this.authorizationManager.getJeaEndpoint(connection.name)
                    };
                    if (Net.isUnauthorized(error)) {
                        liveConnection.status = {
                            type: LiveConnectionStatusType.Unauthorized
                        };
                    } else if (Net.isForbidden(error)) {
                        liveConnection.status = {
                            type: LiveConnectionStatusType.Forbidden
                        };
                    } else if (error.status === HttpStatusCode.NotFound || error.status === HttpStatusCode.BadRequest) {
                        // failed connect to target node
                        const alias = this.connectionManager.getActiveAlias(connection.name);
                        // found alias, retry
                        if (alias) {
                            return this.getConnectionStatus(statusProvider, connection, alias);
                        } else { // no alias, return error

                            // For Badrequest(400), try to extract the PS error code.
                            if (error.status === HttpStatusCode.BadRequest) {
                                liveConnection.status = {
                                    type: LiveConnectionStatusType.Fatal,
                                    details: Net.getErrorMessage(error)
                                };
                            } else { // else just show the default connection failure error.
                                const label = this.connectionStrings.NoConnection.label;
                                const message = this.connectionStrings.NoConnection.message;
                                liveConnection.status = {
                                    label: label,
                                    type: LiveConnectionStatusType.Unknown,
                                    details: message
                                };
                            }
                        }
                    } else {
                        liveConnection.status = {
                            type: LiveConnectionStatusType.Fatal,
                            details: Net.getErrorMessage(error)
                        };
                    }
                    return of(liveConnection);
                }),
                tap(liveConnection => {
                    if (!liveConnection.status.label) {
                        liveConnection.status.label = this.statusLabelMap[liveConnection.status.type]
                            || this.statusLabelMap[LiveConnectionStatusType.Unknown];
                    }
                }));
    }

    /**
     * Retrieves a connections status from a powershell status provider
     * @param nodeName the node name.
     * @param options The powershell options.
     */
    private getConnectionStatusFromPowershell(
        nodeName: string,
        options: PowerShellCommand): Observable<ConnectionStatusResult> {
        if (!nodeName) {
            // log warning about this condition
            const message = this.connectionStrings.ErrorNodeName.message;
            Logging.log({
                source: 'ConnectionStream',
                level: LogLevel.Error,
                message: message.format(nodeName)
            });
            return throwError(() => new Error(message));
        }

        const psSession = this.powershellConnection.createAutomaticSession(nodeName, { noAuth: true });
        return this.powershellConnection.run(psSession, options.command ? options : options.script)
            .pipe(
                map(response => {
                    // we expect the script to return an object as the first result.
                    // The object should have some property called 'status' but we will check this later.
                    return response.results[0];
                }));
    }

    /**
     * Retrieves a connections status from a gatewayUrl status provider
     * @param nodeName the node name.
     * @param relativeUrl the relative url from the relativeGatewayUrl provider.
     */
    private getConnectionStatusFromGatewayUrl(nodeName: string, relativeUrl: string): Observable<ConnectionStatusResult> {
        if (!relativeUrl) {
            // log warning about this condition
            const message = this.connectionStrings.ErrorGatewayUrl.message;
            Logging.logError('ConnectionStream', message);
            return throwError(() => new Error(message));
        }

        relativeUrl = relativeUrl.replace('$connectionName', nodeName);

        // scan GWv2 API and use nodeConnection API so it can include authorization information.
        // /Services/LinuxBase/ssh/nodes/$connectionName/connectionstatus
        const segments = MsftSme.trimStart(relativeUrl, '/').split('/');
        if (segments.length > 4 && segments[0].localeCompareIgnoreCase('services') === 0) {
            segments.shift();
            const serviceName = segments.shift();
            const controllerName = segments.shift();
            segments.shift();
            segments.shift();
            const remained = segments.join('/');
            return this.nodeConnection.get({ serviceName, controllerName,  nodeName }, remained, <NodeRequest>{ noAuth: true });
        }

        return this.gatewayConnection.get(relativeUrl);
    }

    /**
     * Retrieves a connections status from a extension service method
     */
    private getConnectionStatusFromService(nodeName: string, methodId: ExtensionMethodIdentifier): Observable<ConnectionStatusResult> {
        if (!methodId) {
            // log warning about this condition
            const message = this.connectionStrings.ErrorService.message;
            Logging.log({
                source: 'ConnectionStream',
                level: LogLevel.Error,
                message: message
            });
            return throwError(() => new Error(message));
        }

        const entryPointId = EnvironmentModule.createEntrypointId(methodId.module, methodId.name);
        return this.extensionBroker.callService(entryPointId, methodId.method, methodId.version, nodeName);
    }

    /**
     * Retrieves a connections status from a extension worker method
     */
    private getConnectionStatusFromWorker(nodeName: string, methodId: ExtensionMethodIdentifier): Observable<ConnectionStatusResult> {
        if (!methodId) {
            // log warning about this condition
            const message = this.connectionStrings.ErrorWorker.message;
            Logging.log({
                source: 'ConnectionStream',
                level: LogLevel.Error,
                message: message
            });
            return throwError(() => new Error(message));
        }

        const entryPointId = EnvironmentModule.createEntrypointId(methodId.module, methodId.name);
        return this.extensionBroker.runWorker(entryPointId, methodId.method, methodId.version, nodeName);
    }
}
